import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  LockClosedIcon,
  ClockIcon,
  ExclamationCircleIcon,
  LinkSlashIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import SharedSessionView from '../components/share/SharedSessionView';
import truckImage from '../assets/amesTruck.png';
import acLogo from '../assets/aclogo.png';

// API base URL for edge functions
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SHARE_API = `${SUPABASE_URL}/functions/v1/share`;

const SharedSessionPage = () => {
  const { token } = useParams();

  // State machine: loading | password | expired | notfound | error | ready
  const [status, setStatus] = useState('loading');
  
  // Data
  const [sessionData, setSessionData] = useState(null);
  const [shareInfo, setShareInfo] = useState(null);
  
  // Error info
  const [errorMessage, setErrorMessage] = useState(null);
  
  // Password state
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Fetch share data
  const fetchShareData = useCallback(async (passwordAttempt = null) => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      
      if (passwordAttempt) {
        headers['x-share-password'] = passwordAttempt;
      }

      const response = await fetch(`${SHARE_API}/${token}`, { headers });
      const data = await response.json();

      if (response.ok) {
        setSessionData(data);
        setShareInfo(data.share);
        setStatus('ready');
        return { success: true };
      }

      switch (data.error) {
        case 'password_required':
          setShareInfo({
            contact_email: data.contact_email,
            contact_name: data.contact_name,
          });
          setStatus('password');
          return { success: false, error: 'password_required' };

        case 'invalid_password':
          setPasswordError('Incorrect password. Please try again.');
          setStatus('password');
          return { success: false, error: 'invalid_password' };

        case 'expired':
          setShareInfo({
            expires_at: data.expires_at,
            contact_email: data.contact_email,
            contact_name: data.contact_name,
          });
          setStatus('expired');
          return { success: false };

        case 'not_found':
        case 'invalid_token':
          setStatus('notfound');
          return { success: false };

        default:
          setErrorMessage(data.error || 'Unknown error');
          setStatus('error');
          return { success: false };
      }
    } catch (err) {
      console.error('Share fetch error:', err);
      setErrorMessage(err.message || 'Failed to load share data');
      setStatus('error');
      return { success: false };
    }
  }, [token]);

  // Initial load
  useEffect(() => {
    if (token) {
      fetchShareData();
    } else {
      setStatus('notfound');
    }
  }, [token, fetchShareData]);

  // Password submit handler
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!password.trim()) {
      setPasswordError('Please enter a password');
      return;
    }

    setPasswordLoading(true);
    setPasswordError(null);

    const result = await fetchShareData(password);

    if (!result.success && result.error === 'invalid_password') {
      setPassword('');
    }

    setPasswordLoading(false);
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // ============================================
  // RENDER: Loading
  // ============================================
  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-sm text-gray-500">Loading shared report...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Password Required
  // ============================================
  if (status === 'password') {
    return (
      <div className="min-h-screen flex">
        {/* Left - Form */}
        <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            <div className="text-left mb-8">
              <img className="h-10 w-auto" src={acLogo} alt="Ames Connect" />
              <h3 className="mt-6 text-2xl font-bold text-gray-900">
                Protected Report
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                This shared report requires a password to view.
              </p>
            </div>

            <form className="space-y-5" onSubmit={handlePasswordSubmit}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className={`w-full px-3 py-2.5 border rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 pr-10 ${
                      passwordError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    autoFocus
                    disabled={passwordLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {passwordError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-sm">
                  <div className="flex">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-400 mr-2 flex-shrink-0" />
                    <p className="text-sm text-red-700">{passwordError}</p>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={!password || passwordLoading}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {passwordLoading ? 'Verifying...' : 'View Report'}
              </button>

              {shareInfo?.contact_email && (
                <p className="text-center text-sm text-gray-500">
                  Need the password?{' '}
                  <a
                    href={`mailto:${shareInfo.contact_email}`}
                    className="font-medium text-red-600 hover:text-red-500"
                  >
                    Contact {shareInfo.contact_name || 'sender'}
                  </a>
                </p>
              )}
            </form>
          </div>
        </div>

        {/* Right - Branding */}
        <div className="hidden lg:flex w-0 flex-1 bg-gray-50 items-center justify-center p-12">
          <div className="w-full max-w-md text-center">
            <img
              className="mx-auto h-auto w-full max-w-sm"
              src={truckImage}
              alt="Ames Profiler Truck"
            />
            <h2 className="mt-8 text-2xl font-bold text-gray-900">Pavement Analysis, Perfected.</h2>
            <p className="mt-3 text-base text-gray-600">
              Professional road profiling data shared securely through Ames Connect.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Link Expired
  // ============================================
  if (status === 'expired') {
    return (
      <div className="min-h-screen flex">
        {/* Left - Content */}
        <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            <div className="text-left mb-8">
              <img className="h-10 w-auto" src={acLogo} alt="Ames Connect" />
              <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <ClockIcon className="h-5 w-5 text-amber-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  Link Expired
                </h3>
              </div>
              <p className="mt-4 text-sm text-gray-600">
                This shared report expired on <strong>{formatDate(shareInfo?.expires_at)}</strong> and is no longer accessible.
              </p>
            </div>

            {shareInfo?.contact_email && (
              <div className="space-y-4">
                <p className="text-sm text-gray-500">
                  You can request a new link from the person who shared this report with you.
                </p>
                <a
                  href={`mailto:${shareInfo.contact_email}?subject=Request%20new%20shared%20link`}
                  className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
                >
                  Contact {shareInfo.contact_name || 'Sender'}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Right - Branding */}
        <div className="hidden lg:flex w-0 flex-1 bg-gray-50 items-center justify-center p-12">
          <div className="w-full max-w-md text-center">
            <img
              className="mx-auto h-auto w-full max-w-sm"
              src={truckImage}
              alt="Ames Profiler Truck"
            />
            <h2 className="mt-8 text-2xl font-bold text-gray-900">Pavement Analysis, Perfected.</h2>
            <p className="mt-3 text-base text-gray-600">
              Professional road profiling data shared securely through Ames Connect.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Not Found
  // ============================================
  if (status === 'notfound') {
    return (
      <div className="min-h-screen flex">
        {/* Left - Content */}
        <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            <div className="text-left mb-8">
              <img className="h-10 w-auto" src={acLogo} alt="Ames Connect" />
              <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <LinkSlashIcon className="h-5 w-5 text-gray-500" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  Link Not Found
                </h3>
              </div>
              <p className="mt-4 text-sm text-gray-600">
                This shared link doesn't exist or has been revoked by the owner.
              </p>
            </div>

            <p className="text-sm text-gray-500">
              Please contact the person who shared this link with you to request a new one.
            </p>
          </div>
        </div>

        {/* Right - Branding */}
        <div className="hidden lg:flex w-0 flex-1 bg-gray-50 items-center justify-center p-12">
          <div className="w-full max-w-md text-center">
            <img
              className="mx-auto h-auto w-full max-w-sm"
              src={truckImage}
              alt="Ames Profiler Truck"
            />
            <h2 className="mt-8 text-2xl font-bold text-gray-900">Pavement Analysis, Perfected.</h2>
            <p className="mt-3 text-base text-gray-600">
              Professional road profiling data shared securely through Ames Connect.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Error
  // ============================================
  if (status === 'error') {
    return (
      <div className="min-h-screen flex">
        {/* Left - Content */}
        <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            <div className="text-left mb-8">
              <img className="h-10 w-auto" src={acLogo} alt="Ames Connect" />
              <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <ExclamationCircleIcon className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  Something Went Wrong
                </h3>
              </div>
              <p className="mt-4 text-sm text-gray-600">
                {errorMessage || 'An unexpected error occurred while loading this report.'}
              </p>
            </div>

            <button
              onClick={() => {
                setStatus('loading');
                fetchShareData();
              }}
              className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>

        {/* Right - Branding */}
        <div className="hidden lg:flex w-0 flex-1 bg-gray-50 items-center justify-center p-12">
          <div className="w-full max-w-md text-center">
            <img
              className="mx-auto h-auto w-full max-w-sm"
              src={truckImage}
              alt="Ames Profiler Truck"
            />
            <h2 className="mt-8 text-2xl font-bold text-gray-900">Pavement Analysis, Perfected.</h2>
            <p className="mt-3 text-base text-gray-600">
              Professional road profiling data shared securely through Ames Connect.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Ready - Data Loaded Successfully
  // ============================================
  if (status === 'ready' && sessionData) {
    return (
      <SharedSessionView
        data={sessionData}
        shareInfo={shareInfo}
        token={token}
      />
    );
  }

  // Fallback
  return null;
};

export default SharedSessionPage;